/* eslint-disable camelcase */
import React from 'react'
import PropTypes from 'prop-types'
import { richTextToComponents } from '../../utils/gatsby/richTextToComponents'
import { BodyCopy, Container } from '../Globals'
import { Row } from '@components/Common'
import { StyledEventButton, StyledEventButtonCol } from '../Button/style'
import { StyledA, EventDirections } from '../EventDetail/style'
import Html from '../Html'
import { MapElements } from './MapElements'
import { GrowthPyramid } from './GrowthPyramid'
import { getDirections, handleEventTimes } from '@utils'
import A from '@components/A'
import Box from '@components/Box'
import { StyledVideo } from './style'

const EventBody = props => {
  const { url, file, eventDetail, saveFile } = props
  const isPastEvent = props.isPastEvent

  const videoOrAudioUrl = props?.videoOrAudioFile?.file?.url
  const videoOrAudioTitle = props?.videoOrAudioTitle
  const isVideo = videoOrAudioUrl?.endsWith('mp4')

  const content = props?.content
  const contentHtml = props?.contentHtml
  const eventStart = props?.eventStart
  const eventEnd = props?.eventEnd
  const timeZone = props?.timeZone
  const location = props?.location
  let street = props?.street
  const city = props?.city
  const state = props?.state
  const registrationLink = props?.registrationLink
  const country = props?.country
  const eventTimes = props?.eventTimes
  const signUpForFutureEventsLink = props?.signUpForFutureEventsLink

  const hasEventTimes = eventTimes?.length

  const eventDurations = handleEventTimes(
    hasEventTimes
      ? eventTimes
      : eventStart && eventEnd
      ? [{ eventStart, eventEnd, timeZone }]
      : []
  )

  if (location === street) street = null

  const address = {
    street: street || location,
    city: city || '',
    state: state || '',
    country: country || '',
  }

  const showContent = () => {
    if (contentHtml?.useHTMLField === 'true' && contentHtml.html) {
      // show raw HTML
      return <Html contentHtml={contentHtml} />
    } // show rich text
    else {
      return richTextToComponents(content, {
        optionalId: props.preview?.content?.__typename,
        optionalType: props.preview?.content?.links,
      })
    }
  }

  return (
    <>
      {eventDurations?.map(({ eventString, timeDurationString }, i) => (
        <>
          <BodyCopy marginTop={i === 0 ? 50 : 4}>
            <b>{eventString}</b>
          </BodyCopy>
          {timeDurationString && (
            <BodyCopy>
              <b>{timeDurationString} </b>
            </BodyCopy>
          )}
        </>
      ))}
      <EventDirections
        onClick={() => address && address.street && getDirections(address)}
      >
        <BodyCopy marginTop={40}>{location || ''}</BodyCopy>
        <BodyCopy>{street || ''}</BodyCopy>
        <BodyCopy>
          {(city && `${city}`) || ''} {(state && `, ${state}`) || ''}
        </BodyCopy>
      </EventDirections>
      {videoOrAudioUrl && (
        <Box mb="39px">
          {videoOrAudioTitle && (
            <BodyCopy mb="8px" fontSize="18px" fontWeight={700}>
              {videoOrAudioTitle}
            </BodyCopy>
          )}
          {isVideo ? (
            <StyledVideo width="100%" controls src={videoOrAudioUrl} />
          ) : (
            <audio controls src={videoOrAudioUrl} />
          )}
        </Box>
      )}
      {showContent()}
      <Container marginTop={40} fluid>
        <Row>
          {registrationLink && !isPastEvent && (
            <StyledA href={registrationLink}>
              <StyledEventButtonCol size="small" marginBottomCol="10px" active>
                REGISTER FOR THE EVENT
              </StyledEventButtonCol>
            </StyledA>
          )}
          {eventDetail && !isPastEvent && location && (
            <StyledA
              onClick={() => saveFile(eventDetail, file)}
              href={url}
              download={file}
            >
              <StyledEventButtonCol
                border="2px solid #B4A06E"
                marginCol="15px"
                color="#B4A06E"
                size="small"
                hoverColor
              >
                ADD EVENT TO CALENDAR
              </StyledEventButtonCol>
            </StyledA>
          )}
          {isPastEvent && (
            <Box display="flex" alignItems="center" justifyContent="center">
              <BodyCopy
                color="gold"
                fontSize="16px"
                fontWeight={700}
                mb="16px"
                mr="41px"
              >
                EVENT ENDED
              </BodyCopy>
              {signUpForFutureEventsLink && (
                <A href={signUpForFutureEventsLink}>
                  <StyledEventButton active px={2}>
                    SIGN UP FOR OTHER EVENTS
                  </StyledEventButton>
                </A>
              )}
            </Box>
          )}
        </Row>
      </Container>
    </>
  )
}

const ArticleBody = ({
  __typename,
  contentful_id,
  preview,
  contentHtml,
  content,
  category,
  slug,
  pageSlugs,
  ...rest
}) => {
  // redirect to TGB page if this is a TGB article
  if (typeof window !== 'undefined' && category === 'tomorrows-global-business')
    window.location.href = '/tomorrows-global-business'

  if (__typename === 'ContentfulEvent' || __typename === 'event') {
    return (
      <EventBody
        preview={preview}
        content={content}
        contentHtml={contentHtml}
        {...rest}
      />
    )
  }

  if (
    contentful_id === '8rHDuF0GyvQ61Qavi837q' || // add pyramid if Growth in a New Era page
    preview?.sys?.id === '8rHDuF0GyvQ61Qavi837q'
  ) {
    return <GrowthPyramid contentHtml={contentHtml} />
  }

  const additionalHeader =
    contentful_id === '3MFbNstUEvx7nt40Xy6gg5' || // add map elements if US Court Closings article
    preview?.sys?.id === '3MFbNstUEvx7nt40Xy6gg5'

  if (contentHtml?.html && contentHtml?.useHTMLField === 'true') {
    // show raw HTML
    return (
      <>
        {additionalHeader && <MapElements />}
        <Html contentHtml={contentHtml} />
      </>
    )
  } // show rich text
  else {
    return (
      <>
        {additionalHeader && <MapElements />}
        {richTextToComponents(content, {
          optionalId: preview?.content?.__typename,
          optionalType: preview?.content?.links,
          slugPath: {
            slug: slug,
            category: category,
            pageSlugs: pageSlugs,
            type: __typename,
          },
        })}
      </>
    )
  }
}

ArticleBody.propTypes = {
  content: PropTypes.object,
}

export default ArticleBody
